// Form validation utility functions

export const validateForm = (
  formData,
  bookingType = "instant",
  hospitalId = null,
  userRole = null
) => {
  const errors = {};

  // Patient Name validation (optional)
  if (formData.patientName && formData.patientName.trim().length > 0) {
    if (formData.patientName.trim().length < 2) {
      errors.patientName = "Patient name must be at least 2 characters long";
    } else if (formData.patientName.length > 100) {
      errors.patientName = "Patient name cannot exceed 100 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.patientName.trim())) {
      errors.patientName = "Patient name can only contain letters and spaces";
    }
  }

  // HMIS ID validation (optional)
  if (formData.hmisId && formData.hmisId.trim().length > 0) {
    if (formData.hmisId.trim().length < 3) {
      errors.hmisId = "HMIS ID must be at least 3 characters long";
    } else if (formData.hmisId.length > 50) {
      errors.hmisId = "HMIS ID cannot exceed 50 characters";
    }
  }

  // Patient Age validation (optional)
  if (formData.patientAge && formData.patientAge.trim().length > 0) {
    const age = parseInt(formData.patientAge);
    if (isNaN(age)) {
      errors.patientAge = "Please enter a valid age";
    } else if (age < 0) {
      errors.patientAge = "Patient age cannot be negative";
    } else if (age > 150) {
      errors.patientAge = "Patient age cannot exceed 150 years";
    }
  }

  // Relative Name validation (optional)
  if (formData.relativeName && formData.relativeName.trim().length > 0) {
    if (formData.relativeName.trim().length < 2) {
      errors.relativeName = "Relative name must be at least 2 characters long";
    } else if (formData.relativeName.length > 100) {
      errors.relativeName = "Relative name cannot exceed 100 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.relativeName.trim())) {
      errors.relativeName = "Relative name can only contain letters and spaces";
    }
  }

  // Current Condition validation (optional)
  if (
    formData.currentCondition &&
    formData.currentCondition.trim().length > 0
  ) {
    if (formData.currentCondition.trim().length < 10) {
      errors.currentCondition =
        "Please provide a detailed description of the patient's condition (at least 10 characters)";
    } else if (formData.currentCondition.length > 500) {
      errors.currentCondition =
        "Condition description cannot exceed 500 characters";
    }
  }

  // Phone number validation (optional)
  if (formData.contactPhone && formData.contactPhone.trim().length > 0) {
    const phoneInput = formData.contactPhone.trim();
    // Remove spaces, dashes, parentheses but keep + and other characters to detect them
    const cleanPhone = phoneInput.replace(/[\s\-\(\)]/g, "");

    // Check if it contains + symbol or is not exactly 10 digits
    if (cleanPhone.includes("+") || cleanPhone.length !== 10) {
      errors.contactPhone =
        "Please enter exactly 10 digits only (no country code, no +91)";
    } else if (!/^\d{10}$/.test(cleanPhone)) {
      errors.contactPhone = "Please enter exactly 10 digits only";
    }
  }

  // Country code validation (if provided)
  if (formData.contactPhone_country_code) {
    if (!/^\+[1-9]\d{0,3}$/.test(formData.contactPhone_country_code)) {
      errors.contactPhone_country_code = "Please select a valid country code";
    }
  }

  // Pickup Address validation
  if (!formData.pickupAddress || formData.pickupAddress.trim().length < 5) {
    errors.pickupAddress = "Pickup address must be at least 5 characters long";
  } else if (formData.pickupAddress.length > 255) {
    errors.pickupAddress = "Pickup address cannot exceed 255 characters";
  }

  // Destination Address validation
  if (
    !formData.destinationAddress ||
    formData.destinationAddress.trim().length < 5
  ) {
    errors.destinationAddress =
      "Destination address must be at least 5 characters long";
  } else if (formData.destinationAddress.length > 255) {
    errors.destinationAddress =
      "Destination address cannot exceed 255 characters";
  }

  // Ambulance Type validation
  if (!formData.ambulanceType) {
    errors.ambulanceType = "Please select an ambulance type";
  }

  // Hospital validation for SUPER_ADMIN
  if (userRole === "SUPER_ADMIN" && !hospitalId) {
    errors.hospital = "Please select a hospital";
  }

  // Scheduled booking validations
  if (bookingType === "scheduled") {
    if (!formData.scheduledDate) {
      errors.scheduledDate = "Please select a date for scheduled booking";
    } else {
      const selectedDate = new Date(formData.scheduledDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        errors.scheduledDate = "Scheduled date cannot be in the past";
      }

      // Check if date is too far in the future (e.g., more than 30 days)
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 30);
      if (selectedDate > maxDate) {
        errors.scheduledDate =
          "Scheduled date cannot be more than 30 days in the future";
      }
    }

    if (!formData.scheduledTime) {
      errors.scheduledTime = "Please select a time for scheduled booking";
    } else if (formData.scheduledDate) {
      // If it's today, check if time is in the future
      const selectedDateTime = new Date(
        `${formData.scheduledDate}T${formData.scheduledTime}`
      );
      const now = new Date();

      if (selectedDateTime <= now) {
        errors.scheduledTime = "Scheduled time must be in the future";
      }
    }
  }

  // Special Requirements validation (optional but with limits)
  if (
    formData.specialRequirements &&
    formData.specialRequirements.length > 500
  ) {
    errors.specialRequirements =
      "Special requirements cannot exceed 500 characters";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateField = (fieldName, value, formData = {}) => {
  const errors = {};

  switch (fieldName) {
    case "patientName":
      if (!value || value.trim().length < 2) {
        errors.patientName = "Patient name must be at least 2 characters long";
      } else if (value.length > 100) {
        errors.patientName = "Patient name cannot exceed 100 characters";
      } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
        errors.patientName = "Patient name can only contain letters and spaces";
      }
      break;

    case "patientAge":
      const age = parseInt(value);
      if (!value || isNaN(age)) {
        errors.patientAge = "Patient age is required";
      } else if (age < 0) {
        errors.patientAge = "Patient age cannot be negative";
      } else if (age > 150) {
        errors.patientAge = "Patient age cannot exceed 150 years";
      }
      break;

    case "hmisId":
      if (!value || value.trim().length < 3) {
        errors.hmisId =
          "HMIS ID is required and must be at least 3 characters long";
      } else if (value.length > 50) {
        errors.hmisId = "HMIS ID cannot exceed 50 characters";
      }
      break;

    case "relativeName":
      if (!value || value.trim().length < 2) {
        errors.relativeName =
          "Relative name must be at least 2 characters long";
      } else if (value.length > 100) {
        errors.relativeName = "Relative name cannot exceed 100 characters";
      } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
        errors.relativeName =
          "Relative name can only contain letters and spaces";
      }
      break;

    case "currentCondition":
      if (!value || value.trim().length < 10) {
        errors.currentCondition =
          "Please provide a detailed description (at least 10 characters)";
      } else if (value.length > 500) {
        errors.currentCondition = "Description cannot exceed 500 characters";
      }
      break;

    case "contactPhone":
      if (!value || value.trim().length === 0) {
        errors.contactPhone = "Contact phone number is required";
      } else {
        const phoneInput = value.trim();
        // Remove spaces, dashes, parentheses but keep + and other characters to detect them
        const cleanPhone = phoneInput.replace(/[\s\-\(\)]/g, "");

        // Check if it contains + symbol or is not exactly 10 digits
        if (cleanPhone.includes("+") || cleanPhone.length !== 10) {
          errors.contactPhone =
            "Please enter exactly 10 digits only (no country code, no +91)";
        } else if (!/^\d{10}$/.test(cleanPhone)) {
          errors.contactPhone = "Please enter exactly 10 digits only";
        }
      }
      break;

    case "pickupAddress":
      if (!value || value.trim().length < 5) {
        errors.pickupAddress =
          "Pickup address must be at least 5 characters long";
      } else if (value.length > 255) {
        errors.pickupAddress = "Pickup address cannot exceed 255 characters";
      }
      break;

    case "destinationAddress":
      if (!value || value.trim().length < 5) {
        errors.destinationAddress =
          "Destination address must be at least 5 characters long";
      } else if (value.length > 255) {
        errors.destinationAddress =
          "Destination address cannot exceed 255 characters";
      }
      break;

    case "scheduledDate":
      if (!value) {
        errors.scheduledDate = "Please select a date for scheduled booking";
      } else {
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
          errors.scheduledDate = "Scheduled date cannot be in the past";
        }

        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + 30);
        if (selectedDate > maxDate) {
          errors.scheduledDate =
            "Scheduled date cannot be more than 30 days in the future";
        }
      }
      break;

    case "scheduledTime":
      if (!value) {
        errors.scheduledTime = "Please select a time for scheduled booking";
      } else if (formData.scheduledDate) {
        const selectedDateTime = new Date(`${formData.scheduledDate}T${value}`);
        const now = new Date();

        if (selectedDateTime <= now) {
          errors.scheduledTime = "Scheduled time must be in the future";
        }
      }
      break;

    case "specialRequirements":
      if (value && value.length > 500) {
        errors.specialRequirements =
          "Special requirements cannot exceed 500 characters";
      }
      break;

    default:
      break;
  }

  return errors[fieldName] || null;
};

export const getFieldStatus = (fieldName, errors) => {
  if (errors[fieldName]) {
    return "error";
  }
  return "default";
};

export const formatValidationErrors = (validationErrors) => {
  if (!validationErrors || typeof validationErrors !== "object") {
    return "Please check your input and try again";
  }

  if (validationErrors.errors && Array.isArray(validationErrors.errors)) {
    // Server-side validation errors format
    return validationErrors.errors.map((error) => error.message).join(". ");
  }

  if (typeof validationErrors === "object") {
    // Client-side validation errors format
    const errorMessages = Object.values(validationErrors).filter(Boolean);
    return errorMessages.join(". ");
  }

  return validationErrors.toString();
};

// Enhanced error extraction function for new backend validation format
export const extractBackendValidationErrors = (error) => {
  // Handle new structured Joi validation errors from backend
  if (error?.response?.data?.type === "VALIDATION_ERROR") {
    const data = error.response.data;
    const fieldErrors = {};

    if (data.errors && Array.isArray(data.errors)) {
      data.errors.forEach((err) => {
        fieldErrors[err.field] = err.message;
      });
    }

    return {
      hasErrors: true,
      fieldErrors,
      generalMessage: data.message || "Please fix the validation errors below",
      errorType: "VALIDATION_ERROR",
    };
  }

  // Handle other error formats
  if (error?.response?.data?.message) {
    return {
      hasErrors: true,
      fieldErrors: {},
      generalMessage: error.response.data.message,
      errorType: "API_ERROR",
    };
  }

  if (error?.message) {
    return {
      hasErrors: true,
      fieldErrors: {},
      generalMessage: error.message,
      errorType: "GENERAL_ERROR",
    };
  }

  return {
    hasErrors: true,
    fieldErrors: {},
    generalMessage: "An unexpected error occurred. Please try again.",
    errorType: "UNKNOWN_ERROR",
  };
};

export default {
  validateForm,
  validateField,
  getFieldStatus,
  formatValidationErrors,
};
